import { useEffect, useRef } from "react";
import gsap from "gsap";
import { useReducedMotion } from "../lib/motion";

/**
 * Beyond Sure — ambient hexagon decoration.
 *
 * The hexagon shape is canonical BeyondSure brand decoration. Evidence:
 *   • https://insurance.beyondsure.in/customer/login renders large mint-green
 *     hexagons in the corners (the existing live login).
 *   • The logo itself uses a stylized hexagonal shield form.
 *
 * On the forest-green desktop canvas the hexagons are rendered at low opacity
 * in paper-08/paper-12; GSAP drifts each one VERY slowly so it reads as
 * atmosphere, not animation. On the mint mobile/tablet canvas the same shapes
 * are rendered in spring-green at low opacity for the same effect.
 *
 * Props let the caller place 3 hexagons (top-left / mid-right / bottom-left)
 * but the placement is intentionally fixed inside the component so every
 * AuthShell instance reads identically.
 *
 * Accessibility: aria-hidden purely-decorative. Motion respects
 * prefers-reduced-motion (GSAP timelines are not started at all when reduced).
 */
interface HexAmbientProps {
  /** "dark" → paint hexagons in paper-tints (for green canvas).
   *  "light" → paint hexagons in brand-green tints (for mint canvas). */
  tone: "dark" | "light";
  /** Overall opacity multiplier. Defaults to 1. */
  intensity?: number;
}

function Hex({
  size,
  fill,
  stroke,
}: {
  size: number;
  fill: string;
  stroke: string;
}) {
  // Regular hexagon, pointy-top, in a 100x100 viewBox.
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path
        d="M50 4 L91 28 L91 72 L50 96 L9 72 L9 28 Z"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.5"
      />
    </svg>
  );
}

export function HexAmbient({ tone, intensity = 1 }: HexAmbientProps) {
  const reduced = useReducedMotion();
  const a = useRef<HTMLDivElement | null>(null);
  const b = useRef<HTMLDivElement | null>(null);
  const c = useRef<HTMLDivElement | null>(null);

  // Slow drifting motion. Each hexagon moves on its own timeline so they never
  // sync up — that's what makes it feel like atmosphere rather than animation.
  useEffect(() => {
    if (reduced) return;
    const el1 = a.current;
    const el2 = b.current;
    const el3 = c.current;
    if (!el1 || !el2 || !el3) return;
    const tA = gsap.to(el1, {
      y: "+=18", x: "+=10", rotate: "+=6",
      duration: 14, yoyo: true, repeat: -1, ease: "sine.inOut",
    });
    const tB = gsap.to(el2, {
      y: "-=22", x: "-=14", rotate: "-=8",
      duration: 18, yoyo: true, repeat: -1, ease: "sine.inOut",
    });
    const tC = gsap.to(el3, {
      y: "+=12", x: "-=8", rotate: "+=4",
      duration: 22, yoyo: true, repeat: -1, ease: "sine.inOut",
    });
    return () => { tA.kill(); tB.kill(); tC.kill(); };
  }, [reduced]);

  // Color picks per tone.
  const fill =
    tone === "dark" ? "rgb(255 255 255 / 0.045)" : "rgb(62 142 91 / 0.10)";
  const stroke =
    tone === "dark" ? "rgb(255 255 255 / 0.10)" : "rgb(62 142 91 / 0.22)";

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ opacity: intensity }}
    >
      {/* top-left hex — large */}
      <div
        ref={a}
        className="absolute -left-[8vw] -top-[6vh]"
        style={{ willChange: "transform" }}
      >
        <Hex size={520} fill={fill} stroke={stroke} />
      </div>
      {/* mid-right hex — medium */}
      <div
        ref={b}
        className="absolute right-[-12vw] top-[28vh]"
        style={{ willChange: "transform" }}
      >
        <Hex size={420} fill={fill} stroke={stroke} />
      </div>
      {/* bottom-left hex — large */}
      <div
        ref={c}
        className="absolute left-[10vw] bottom-[-15vh]"
        style={{ willChange: "transform" }}
      >
        <Hex size={560} fill={fill} stroke={stroke} />
      </div>
    </div>
  );
}

export default HexAmbient;
